// ─── PART 6: BONDS TAB + ECONOMY TAB ─────────────────────────────────────────
// Dependencies: part1_core (C, s, fmt, bps, fmtB, DataStatus, AnalystButton)
// Exports: BondsTab, EconomyTab
//
// Data sources:
//   FRED API      — Treasury yields (DGS1MO through DGS30), Fed Funds,
//                   SOFR, HY credit spread, 10Y breakeven inflation
//                   All macro series: GDP, CPI, UNRATE, M2SL, INDPRO
//   Treasury.gov  — Debt to the penny (fiscaldata.treasury.gov)
//                   No key required. Always live.
//
// onData callbacks pushed to globalSignals:
//   "yieldSpread" — number (10Y - 2Y in decimal)
//   "debt"        — number (total public debt in dollars)
//
// Yield curve shape analysis:
//   Computes: 2Y-10Y spread, 3M-10Y spread (Estrella-Mishkin model), steepness
//   Flags inversion, near-flat, bear steepener, bull steepener patterns

import { useState, useEffect, useCallback } from "react";
import {
  LineChart, Line, AreaChart, Area,
  XAxis, YAxis, Tooltip, ResponsiveContainer,
  ReferenceLine, ComposedChart,
} from "recharts";
import { AlertCircle, RefreshCw } from "lucide-react";
import {
  C, s, font, fmt, bps, fmtB, fmtK,
  DataStatus, AnalystButton,
  FRED_BASE, TREASURY_BASE,
  backendFetch,
} from "./core";

// ─── YIELD SERIES MANIFEST ────────────────────────────────────────────────────
const YIELD_SERIES = [
  { id: "DGS1MO", label: "1M",  months: 1   },
  { id: "DGS3MO", label: "3M",  months: 3   },
  { id: "DGS6MO", label: "6M",  months: 6   },
  { id: "DGS1",   label: "1Y",  months: 12  },
  { id: "DGS2",   label: "2Y",  months: 24  },
  { id: "DGS5",   label: "5Y",  months: 60  },
  { id: "DGS10",  label: "10Y", months: 120 },
  { id: "DGS30",  label: "30Y", months: 360 },
];

// Additional rate series for context panel
const RATE_SERIES = [
  { id: "FEDFUNDS", label: "Fed Funds Rate",           unit: "%" },
  { id: "SOFR",     label: "SOFR",                     unit: "%" },
  { id: "T10YIE",   label: "10Y Breakeven Inflation",  unit: "%" },
  { id: "BAMLH0A0HYM2", label: "HY Credit Spread",    unit: "%" },
];

// ─── ECONOMY SERIES MANIFEST ──────────────────────────────────────────────────
const ECON_SERIES = [
  { id: "GDP",          label: "GDP",                    unit: "B USD",  freq: "Quarterly" },
  { id: "CPIAUCSL",     label: "CPI — All Items",        unit: "Index",  freq: "Monthly"   },
  { id: "CPILFESL",     label: "Core CPI (ex Food/Energy)",unit:"Index", freq: "Monthly"   },
  { id: "PCEPI",        label: "PCE Price Index",        unit: "Index",  freq: "Monthly"   },
  { id: "UNRATE",       label: "Unemployment Rate",      unit: "%",      freq: "Monthly"   },
  { id: "PAYEMS",       label: "Nonfarm Payrolls",       unit: "K",      freq: "Monthly"   },
  { id: "FEDFUNDS",     label: "Federal Funds Rate",     unit: "%",      freq: "Monthly"   },
  { id: "M2SL",         label: "M2 Money Supply",        unit: "B USD",  freq: "Monthly"   },
  { id: "INDPRO",       label: "Industrial Production",  unit: "Index",  freq: "Monthly"   },
  { id: "SOFR",         label: "SOFR",                   unit: "%",      freq: "Daily"     },
  { id: "T10YIE",       label: "10Y Breakeven Inflation",unit: "%",      freq: "Daily"     },
  { id: "BAMLH0A0HYM2", label: "HY Credit Spread",      unit: "%",      freq: "Daily"     },
  { id: "UMCSENT",      label: "Consumer Sentiment",     unit: "Index",  freq: "Monthly"   },
];

// Short, human-readable names for the Economy tab buttons (limited horizontal
// space on mobile). Falls back to the raw series id for any unlisted series.
const SERIES_DISPLAY_NAMES = {
  GDP:          "GDP",
  CPIAUCSL:     "CPI",
  CPILFESL:     "Core CPI",
  PCEPI:        "PCE",
  UNRATE:       "Unemployment",
  PAYEMS:       "Payrolls",
  FEDFUNDS:     "Fed Funds",
  M2SL:         "M2 Supply",
  INDPRO:       "Industrial",
  SOFR:         "SOFR",
  T10YIE:       "Breakeven",
  BAMLH0A0HYM2: "HY Spread",
  UMCSENT:      "Sentiment",
};

// ─── YIELD CURVE ANALYSIS ─────────────────────────────────────────────────────
function analyzeYieldCurve(yields) {
  const get = (id) => yields[id]?.value;
  const y2  = get("DGS2");
  const y10 = get("DGS10");
  const y3m = get("DGS3MO");
  const y30 = get("DGS30");
  const y5  = get("DGS5");

  if (!y2 || !y10) return null;

  const spread2_10 = y10 - y2;
  const spread3m10 = y10 - (y3m || y2);
  const steepness  = y30 ? y30 - y2 : null;

  let shape, shapeColor, shapeDesc;
  if (spread2_10 < -0.5) {
    shape      = "DEEPLY INVERTED";
    shapeColor = C.red;
    shapeDesc  = "Deep inversion historically associated with more severe recessions. Monitor duration.";
  } else if (spread2_10 < 0) {
    shape      = "INVERTED";
    shapeColor = C.red;
    shapeDesc  = "Inversion present. Every U.S. recession since 1955 was preceded by 2Y-10Y inversion.";
  } else if (spread2_10 < 0.25) {
    shape      = "NEAR-FLAT";
    shapeColor = C.amber;
    shapeDesc  = "Flat curve signals economic deceleration. Watch direction over next 30 days.";
  } else if (y5 && y5 > y2 && y10 > y5) {
    shape      = "NORMAL";
    shapeColor = C.green;
    shapeDesc  = "Upward sloping — typical of expansion. Longer maturities compensate for duration risk.";
  } else {
    shape      = "HUMPED";
    shapeColor = C.amber;
    shapeDesc  = "Mid-curve yields above long end. Can signal peak-rate expectations.";
  }

  return {
    spread2_10,
    spread3m10,
    steepness,
    shape,
    shapeColor,
    shapeDesc,
  };
}

// ─── CUSTOM TOOLTIP ───────────────────────────────────────────────────────────
function CurveTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: C.panel,
      border: `1px solid ${C.border}`,
      borderRadius: 3,
      padding: "6px 10px",
      fontSize: 13,
      fontFamily: font,
    }}>
      <div style={{ color: C.amber, fontWeight: 700 }}>{label}</div>
      {payload.map((p) => (
        <div key={p.name} style={{ color: p.color || C.text }}>
          {p.name}: {fmt(p.value)}%
        </div>
      ))}
    </div>
  );
}

// ─── SPREAD INDICATOR ─────────────────────────────────────────────────────────
function SpreadIndicator({ label, value, desc }) {
  if (value == null) return null;
  const inverted = value < 0;
  const color    = inverted ? C.red : value < 0.25 ? C.amber : C.green;
  return (
    <div style={{
      background: C.panelAlt,
      border:     `1px solid ${color}33`,
      borderLeft: `3px solid ${color}`,
      borderRadius: 3,
      padding: "8px 10px",
    }}>
      <div style={{ fontSize: 11, color: C.textDim, letterSpacing: "0.1em", marginBottom: 2 }}>
        {label}
      </div>
      <div style={{ fontSize: 18, fontWeight: 700, color }}>
        {bps(value)}
      </div>
      <div style={{ fontSize: 11, color: C.textDim, marginTop: 2, lineHeight: 1.5 }}>
        {desc}
      </div>
    </div>
  );
}

// ─── YIELD GRID ───────────────────────────────────────────────────────────────
function YieldGrid({ yields }) {
  return (
    <div style={{
      display: "grid",
      // ⚠⚠ auto-fit + minmax, NOT repeat(8, 1fr). `1fr` is minmax(AUTO, 1fr), and
      // that `auto` floor is the track's MIN-CONTENT width — so eight fixed
      // tracks could never compress below ~524px (8 cells x ~62px + 7 gaps) and
      // pushed this grid past every phone viewport. auto-fit drops the column
      // count instead of overflowing.
      // ⚠ 72px is sized to the WIDEST thing in a cell, which is the value, not
      // the label: fmt() is toFixed(2), so it renders "4.32%" (5 chars) at
      // fontSize 15 bold, or "15.84%" (6) if rates ever get there. JetBrains Mono
      // advances 0.6em, so 6 chars ~= 54px, plus 6px padding ("5px 3px") and 2px
      // border = 62px. 72 clears that with headroom. The labels (1M..30Y, 3 chars
      // at fontSize 10) and the date (MM-DD) are both far narrower.
      // ⚠ 72 also lands the wrap where the curve reads best: 4-across on a phone
      // puts the short end (1M/3M/6M/1Y) on one row and the long end
      // (2Y/5Y/10Y/30Y) on the next, and it returns to 8-across from ~650px, so
      // desktop is unchanged. Safe down to a 320px viewport, which gets 3-across
      // without overflowing.
      // ⚠⚠ UNMEASURED. Every number above is derived from the monospace advance
      // width, not from a real render — this is the one constant here worth
      // checking on a device and adjusting.
      gridTemplateColumns: "repeat(auto-fit, minmax(72px, 1fr))",
      gap: 4,
      marginTop: 10,
    }}>
      {YIELD_SERIES.map((ser) => {
        const y = yields[ser.id];
        return (
          <div key={ser.id} style={{
            textAlign: "center",
            padding: "5px 3px",
            background: "#060a0e",
            borderRadius: 2,
            border: `1px solid ${C.border}`,
          }}>
            <div style={{ fontSize: 10, color: C.textDim, marginBottom: 2 }}>
              {ser.label}
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>
              {y ? `${fmt(y.value)}%` : "—"}
            </div>
            <div style={{ fontSize: 10, color: C.textDim }}>
              {y?.date?.slice(5) || ""}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── BONDS TAB ────────────────────────────────────────────────────────────────
export function BondsTab({ keys, onData }) {
  const [yields,       setYields]       = useState({});
  const [rates,        setRates]        = useState({});
  const [debt,         setDebt]         = useState(null);
  const [yieldHistory, setYieldHistory] = useState([]);
  const [analysis,     setAnalysis]     = useState(null);
  const [status,       setStatus]       = useState("offline");
  const [debtStatus,   setDebtStatus]   = useState("offline");
  const [loading,      setLoading]      = useState(false);

  // ── Fetch Treasury debt (no key required) ──
  useEffect(() => {
    const go = async () => {
      try {
        const res = await backendFetch(
          `${TREASURY_BASE}/accounting/od/debt_to_penny` +
          `?fields=tot_pub_debt_out_amt,record_date` +
          `&sort=-record_date&page[size]=1`,
          "/api/treasury/debt"
        );
        const data = await res.json();
        // Backend shape: { amount, date }. Direct Treasury shape: { data:[{...}] }.
        const amount = data.amount != null
          ? data.amount
          : (data.data?.[0] ? parseFloat(data.data[0].tot_pub_debt_out_amt) : null);
        const date = data.date != null ? data.date : data.data?.[0]?.record_date;
        if (amount != null) {
          setDebt({ amount, date });
          setDebtStatus("live");
          if (onData) onData("debt", amount);
        } else {
          setDebtStatus("offline");
        }
      } catch {
        setDebtStatus("offline");
      }
    };
    go();
  }, []);

  // ── Fetch all yield series from FRED ──
  const fetchYields = useCallback(async () => {
    if (!keys.fredKey && !window.QUANT_SIGNAL_BACKEND) return;
    setLoading(true);
    try {
      // Current yields — one batched multi call
      const yRes = await backendFetch(
        `${FRED_BASE}?series_id=DGS10` +
        `&api_key=${keys.fredKey}&file_type=json&limit=1&sort_order=desc`,
        `/api/fred/multi?series=DGS1MO,DGS3MO,DGS6MO,DGS1,DGS2,DGS5,DGS10,DGS30`
      );
      const yData = await yRes.json();

      const yMap = {};
      YIELD_SERIES.forEach((ser) => {
        const v = yData?.[ser.id];
        if (v && v.value != null && v.value !== ".") {
          yMap[ser.id] = { value: parseFloat(v.value), date: v.date, label: ser.label };
        }
      });
      setYields(yMap);

      // Spread analysis
      const curveAnalysis = analyzeYieldCurve(yMap);
      setAnalysis(curveAnalysis);
      if (onData && curveAnalysis?.spread2_10 != null) {
        onData("yieldSpread", curveAnalysis.spread2_10);
      }

      // Additional rates — one batched multi call
      const rRes = await backendFetch(
        `${FRED_BASE}?series_id=FEDFUNDS` +
        `&api_key=${keys.fredKey}&file_type=json&limit=1&sort_order=desc`,
        `/api/fred/multi?series=${RATE_SERIES.map((s) => s.id).join(",")}`
      );
      const rData = await rRes.json();
      const rMap = {};
      RATE_SERIES.forEach((ser) => {
        const v = rData?.[ser.id];
        if (v && v.value != null && v.value !== ".") {
          rMap[ser.id] = { value: parseFloat(v.value), date: v.date, label: ser.label, unit: ser.unit };
        }
      });
      setRates(rMap);

      setStatus("live");
    } catch {
      setStatus("offline");
    }
    setLoading(false);
  }, [keys.fredKey, onData]);

  // ── Fetch 2Y-10Y spread history (24 months) ──
  const fetchSpreadHistory = useCallback(async () => {
    if (!keys.fredKey && !window.QUANT_SIGNAL_BACKEND) return;
    try {
      const [r2, r10] = await Promise.all([
        backendFetch(`${FRED_BASE}?series_id=DGS2&api_key=${keys.fredKey}&file_type=json&limit=48&sort_order=desc`, `/api/fred/DGS2?limit=48`),
        backendFetch(`${FRED_BASE}?series_id=DGS10&api_key=${keys.fredKey}&file_type=json&limit=48&sort_order=desc`, `/api/fred/DGS10?limit=48`),
      ]);
      const [d2, d10] = await Promise.all([r2.json(), r10.json()]);

      const map2  = {};
      const map10 = {};
      (d2.observations  || []).filter((o) => o.value !== ".").forEach((o) => { map2[o.date]  = parseFloat(o.value); });
      (d10.observations || []).filter((o) => o.value !== ".").forEach((o) => { map10[o.date] = parseFloat(o.value); });

      const dates = Object.keys(map2).filter((d) => map10[d]).sort();
      const history = dates.map((d) => ({
        date:   d.slice(0, 7),
        y2:     map2[d],
        y10:    map10[d],
        spread: map10[d] - map2[d],
      }));
      setYieldHistory(history);
    } catch {}
  }, [keys.fredKey]);

  useEffect(() => {
    fetchYields();
    fetchSpreadHistory();
  }, [fetchYields, fetchSpreadHistory]);

  // Curve data for chart
  const curveData = YIELD_SERIES
    .map((ser) => ({ label: ser.label, yield: yields[ser.id]?.value ?? null }))
    .filter((d) => d.yield !== null);

  const inverted = analysis?.spread2_10 != null && analysis.spread2_10 < 0;

  if (!keys.fredKey && !window.QUANT_SIGNAL_BACKEND) {
    return (
      <div style={{ ...s.panel, ...s.panelB, color: C.textDim, fontSize: 14 }}>
        <AlertCircle size={11} style={{ marginRight: 6, verticalAlign: "middle" }} />
        Data unavailable.
      </div>
    );
  }

  // Analyst context
  const analystContext =
    `Treasury yield curve snapshot:\n` +
    YIELD_SERIES.map((ser) => `${ser.label}: ${yields[ser.id]?.value ?? "N/A"}%`).join(", ") +
    `\n\n2Y-10Y spread: ${analysis ? bps(analysis.spread2_10) : "N/A"}` +
    `\n3M-10Y spread: ${analysis?.spread3m10 != null ? bps(analysis.spread3m10) : "N/A"}` +
    `\nCurve shape: ${analysis?.shape || "N/A"}` +
    `\n\nAdditional rates:` +
    `\nFed Funds: ${rates["FEDFUNDS"]?.value ?? "N/A"}%` +
    `\nSOFR: ${rates["SOFR"]?.value ?? "N/A"}%` +
    `\n10Y Breakeven: ${rates["T10YIE"]?.value ?? "N/A"}%` +
    `\nHY Credit Spread: ${rates["BAMLH0A0HYM2"]?.value ?? "N/A"}%` +
    `\nNational Debt: ${debt ? fmtB(debt.amount) : "N/A"} (as of ${debt?.date || "N/A"})` +
    `\n\nAnalyze: the current curve shape and its historical recession signal strength, ` +
    `what the 3M-10Y spread (Estrella-Mishkin model) implies vs the 2Y-10Y spread, ` +
    `how the HY credit spread interacts with the rate picture, ` +
    `what the breakeven inflation rate implies about Fed credibility, ` +
    `and the overall fixed income regime this data describes.`;

  return (
    <div>
      {/* Top stats row */}
      {/* ⚠⚠ auto-fit + minmax, NOT four fixed tracks. Same shape as OptionsTab's
          top metrics row: four whole s.panels, each a panelH (panelT +
          DataStatus) over a 22px-bold figure. `1fr` is minmax(AUTO, 1fr), so the
          tracks could not compress and four of them wanted ~672px against the
          387px this grid gets on a folded phone in portrait.
          ⚠⚠ THE HEADER ROW IS THE BINDING CONSTRAINT, NOT THE FIGURE — which is
          the opposite of what it looks like, and it is what sets the number:
            panelH   "NATIONAL" (panelT wraps to its longest word; 8ch at
                     fontSize 13 + 0.18em tracking = 10.14px/ch)      ~81px
                   + DataStatus "Treasury" (8ch @ 11px) + dot + gap   ~65px
                   + panelH padding 10 x 2                             20px
                                                                     = ~166px
            panelB   max("OUTSTANDING" 11ch @ s.lbl 7.7 = 85,
                         "$38.42T" 7ch @ 22px bold 13.2 = 92) + 20    = ~112px
          So the cell floor is ~168px, set by the National Debt panel's header,
          and the 22px figure never governs.
          ⚠ 176, NOT the 170 used on Options, and NOT a legibility floor. Options'
          cells bottom out at ~110px, which left 60px to spend on forcing a
          2-across break. These bottom out at ~168 — so the usable window is only
          [168, 180]: below 168 the header clips, above 180 the grid drops to
          3-across at a 768px tablet. 176 sits inside it. This grid has no
          legibility headroom to spend; it is already at its own limit.
          ⚠ Result: 2-across on a phone, 1-across at 320px, 4-across from ~768px
          so desktop is unchanged. Four items over three columns strand one panel
          between roughly 590px and 730px, same unavoidable band as Options.
          ⚠⚠ UNMEASURED — 0.6em advance arithmetic, not a render. It also corrects
          an earlier estimate of ~114px for this cell, which counted only panelB
          and missed that the title and DataStatus sit side by side above it. */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(176px, 1fr))", gap: 8, marginBottom: 10 }}>

        {/* National debt */}
        <div style={s.panel}>
          <div style={s.panelH}>
            <span style={s.panelT}>NATIONAL DEBT</span>
            <DataStatus status={debtStatus} source="" />
          </div>
          <div style={s.panelB}>
            <div style={s.lbl}>TOTAL PUBLIC DEBT OUTSTANDING</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: C.amber, lineHeight: 1 }}>
              {debt ? fmtB(debt.amount) : "—"}
            </div>
            <div style={{ fontSize: 11, color: C.textDim, marginTop: 3 }}>
              As of {debt?.date || "—"}
            </div>
          </div>
        </div>

        {/* 2Y-10Y spread */}
        <div style={s.panel}>
          <div style={s.panelH}>
            <span style={s.panelT}>2Y–10Y SPREAD</span>
            <DataStatus status={status} source="" />
          </div>
          <div style={s.panelB}>
            <div style={s.lbl}>KEY RECESSION INDICATOR</div>
            <div style={{
              fontSize: 22,
              fontWeight: 700,
              color: inverted ? C.red : analysis?.spread2_10 < 0.25 ? C.amber : C.green,
              lineHeight: 1,
            }}>
              {analysis?.spread2_10 != null ? bps(analysis.spread2_10) : "—"}
            </div>
            {analysis && (
              <div style={{ fontSize: 11, fontWeight: 700, color: analysis.shapeColor, marginTop: 3 }}>
                {analysis.shape}
              </div>
            )}
          </div>
        </div>

        {/* Fed Funds */}
        <div style={s.panel}>
          <div style={s.panelH}>
            <span style={s.panelT}>FED FUNDS RATE</span>
            <DataStatus status={status} source="" />
          </div>
          <div style={s.panelB}>
            <div style={s.lbl}>POLICY RATE</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: C.text, lineHeight: 1 }}>
              {rates["FEDFUNDS"] ? `${fmt(rates["FEDFUNDS"].value)}%` : "—"}
            </div>
            <div style={{ fontSize: 11, color: C.textDim, marginTop: 3 }}>
              SOFR: {rates["SOFR"] ? `${fmt(rates["SOFR"].value)}%` : "—"}
            </div>
          </div>
        </div>

        {/* HY Credit spread */}
        <div style={s.panel}>
          <div style={s.panelH}>
            <span style={s.panelT}>HY CREDIT SPREAD</span>
            <DataStatus status={status} source="" />
          </div>
          <div style={s.panelB}>
            <div style={s.lbl}>HIGH YIELD OAS SPREAD</div>
            <div style={{
              fontSize: 22,
              fontWeight: 700,
              lineHeight: 1,
              color: rates["BAMLH0A0HYM2"]?.value > 6 ? C.red
                   : rates["BAMLH0A0HYM2"]?.value > 4 ? C.amber
                   : C.green,
            }}>
              {rates["BAMLH0A0HYM2"] ? `${fmt(rates["BAMLH0A0HYM2"].value)}%` : "—"}
            </div>
            <div style={{ fontSize: 11, color: C.textDim, marginTop: 3 }}>
              {rates["BAMLH0A0HYM2"]?.value > 6
                ? "Stress — credit tightening"
                : rates["BAMLH0A0HYM2"]?.value > 4
                ? "Elevated — watch closely"
                : "Tight — risk-on credit"}
            </div>
          </div>
        </div>
      </div>

      {/* Curve shape analysis */}
      {analysis && (
        <div style={{
          ...s.card(analysis.shapeColor),
          borderLeft: `3px solid ${analysis.shapeColor}`,
          marginBottom: 10,
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: analysis.shapeColor, marginBottom: 4 }}>
                CURVE SHAPE: {analysis.shape}
              </div>
              <div style={{ fontSize: 13, color: C.textMid, lineHeight: 1.7, maxWidth: 600 }}>
                {analysis.shapeDesc}
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, flexShrink: 0, marginLeft: 12 }}>
              {[
                { label: "2Y-10Y", value: analysis.spread2_10, desc: "Primary recession signal" },
                { label: "3M-10Y", value: analysis.spread3m10, desc: "Estrella-Mishkin model" },
                ...(analysis.steepness != null ? [{ label: "2Y-30Y", value: analysis.steepness, desc: "Curve steepness" }] : []),
              ].map((sp) => (
                <SpreadIndicator key={sp.label} label={sp.label} value={sp.value} desc={sp.desc} />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Yield curve chart */}
      <div style={s.panel}>
        <div style={s.panelH}>
          <span style={s.panelT}>U.S. TREASURY YIELD CURVE</span>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <DataStatus status={status} source="" />
            <button style={{ ...s.btn(), padding: "3px 7px" }} onClick={() => { fetchYields(); fetchSpreadHistory(); }}>
              <RefreshCw size={10} />
            </button>
          </div>
        </div>
        <div style={s.panelB}>
          {loading ? (
            <div style={{ color: C.textDim, fontSize: 13, padding: 20, textAlign: "center" }}>
              LOADING YIELD DATA...
            </div>
          ) : curveData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={curveData} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 13, fill: C.textDim }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: C.textDim }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v) => `${v}%`}
                    domain={["auto", "auto"]}
                    width={40}
                  />
                  <Tooltip content={<CurveTooltip />} />
                  <ReferenceLine y={0} stroke={C.border} />
                  <Line
                    type="monotone"
                    dataKey="yield"
                    stroke={inverted ? C.red : C.teal}
                    strokeWidth={2}
                    dot={{ fill: inverted ? C.red : C.teal, r: 4, strokeWidth: 0 }}
                    activeDot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>

              {/* Yield grid */}
              <YieldGrid yields={yields} />

              {/* Analyst */}
              {(keys.anthropicKey || window.QUANT_SIGNAL_BACKEND) && (
                <div style={{ marginTop: 12 }}>
                  <AnalystButton
                    anthropicKey={keys.anthropicKey}
                    panel="yield_curve"
                    label="SYNTHESIZE"
                    context={analystContext}
                  />
                </div>
              )}
            </>
          ) : (
            <div style={{ color: C.textDim, fontSize: 13, padding: 20, textAlign: "center" }}>
              Data unavailable
            </div>
          )}
        </div>
      </div>

      {/* 2Y-10Y Spread History */}
      {yieldHistory.length > 0 && (
        <div style={s.panel}>
          <div style={s.panelH}>
            <span style={s.panelT}>2Y–10Y SPREAD HISTORY — 24 MONTHS</span>
            <DataStatus status={status} source="" />
          </div>
          <div style={s.panelB}>
            <div style={{ fontSize: 11, color: C.textDim, marginBottom: 6 }}>
              Below zero = inverted (red zone). Inversion duration and depth matter as much as the event itself.
            </div>
            <ResponsiveContainer width="100%" height={150}>
              <AreaChart data={yieldHistory} margin={{ top: 4, right: 4, left: 0, bottom: 4 }}>
                <defs>
                  <linearGradient id="spreadPos" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor={C.teal} stopOpacity={0.2} />
                    <stop offset="95%" stopColor={C.teal} stopOpacity={0}   />
                  </linearGradient>
                  <linearGradient id="spreadNeg" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor={C.red} stopOpacity={0.2} />
                    <stop offset="95%" stopColor={C.red} stopOpacity={0}   />
                  </linearGradient>
                </defs>
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 10, fill: C.textDim }}
                  tickLine={false}
                  axisLine={false}
                  interval={Math.floor(yieldHistory.length / 6)}
                />
                <YAxis
                  tick={{ fontSize: 10, fill: C.textDim }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v) => `${(v * 100).toFixed(0)}bps`}
                  domain={["auto", "auto"]}
                  width={52}
                />
                <Tooltip
                  contentStyle={{ background: C.panel, border: `1px solid ${C.border}`, fontSize: 13, fontFamily: font }}
                  formatter={(v) => [`${(v * 100).toFixed(0)}bps`, "2Y-10Y Spread"]}
                />
                <ReferenceLine y={0} stroke={C.red + "66"} strokeWidth={1} />
                <Area
                  type="monotone"
                  dataKey="spread"
                  stroke={inverted ? C.red : C.teal}
                  strokeWidth={1.5}
                  fill={inverted ? "url(#spreadNeg)" : "url(#spreadPos)"}
                  dot={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Rate context panel */}
      {Object.keys(rates).length > 0 && (
        <div style={s.panel}>
          <div style={s.panelH}>
            <span style={s.panelT}>RATE CONTEXT</span>
            <DataStatus status={status} source="" />
          </div>
          <div style={{
            ...s.panelB,
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: 10,
          }}>
            {RATE_SERIES.map((ser) => {
              const r = rates[ser.id];
              if (!r) return null;
              return (
                <div key={ser.id}>
                  <div style={s.lbl}>{ser.label.toUpperCase()}</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: C.text }}>
                    {fmt(r.value)}{ser.unit}
                  </div>
                  <div style={{ fontSize: 10, color: C.textDim }}>{r.date}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div style={{ fontSize: 11, color: C.textDim, marginTop: 4 }}>
        <span style={{ color: "#FFD700", fontWeight: 700 }}>Not a recommendation.</span>
      </div>
    </div>
  );
}

// ─── MONETARY REGIME — helpers ────────────────────────────────────────────────
// Friedman quantity theory: MV = PQ. When money supply (M) times velocity (V)
// outgrows real output (Q), the excess resolves into prices. All computable
// from FRED. Growth rates are annualized over a day-window so the math is
// correct regardless of a series' native cadence (weekly vs monthly vs quarterly).
const MS_DAY = 86_400_000;

function parseObs(observations) {
  if (!Array.isArray(observations)) return [];
  return observations
    .filter((o) => o.value !== "." && o.value != null)
    .map((o) => ({ date: o.date, ts: Date.parse(o.date), value: parseFloat(o.value) }))
    .filter((o) => !isNaN(o.value) && !isNaN(o.ts))
    .sort((a, b) => a.ts - b.ts);
}

function latestOf(arr) { return arr.length ? arr[arr.length - 1] : null; }

// Nearest observation at or before (latest - days). Null if none is old enough.
function baseByDays(arr, days) {
  if (arr.length < 2) return null;
  const latest = arr[arr.length - 1];
  const target = latest.ts - days * MS_DAY;
  for (let i = arr.length - 2; i >= 0; i--) {
    if (arr[i].ts <= target) return arr[i];
  }
  return null;
}

// Annualized % growth over ~`days`. Null when history is insufficient.
function annualizedGrowth(arr, days) {
  const latest = latestOf(arr);
  const base = baseByDays(arr, days);
  if (!latest || !base || base.value <= 0) return null;
  const actualDays = (latest.ts - base.ts) / MS_DAY;
  if (actualDays < days * 0.5) return null; // not enough span to annualize honestly
  const ratio = latest.value / base.value;
  if (ratio <= 0) return null;
  return (Math.pow(ratio, 365 / actualDays) - 1) * 100;
}

// M2 (monthly) × M2V (quarterly), aligned by year-month → nominal demand series.
function buildMV(m2sl, m2v) {
  const byYm = {};
  m2sl.forEach((o) => { byYm[o.date.slice(0, 7)] = o.value; });
  const out = [];
  m2v.forEach((v) => {
    const m = byYm[v.date.slice(0, 7)];
    if (m != null) out.push({ date: v.date, ts: v.ts, value: m * v.value });
  });
  return out;
}

// Detect native frequency from median spacing between observations.
function freqLabel(arr) {
  if (arr.length < 3) return "";
  const gaps = [];
  for (let i = 1; i < arr.length; i++) gaps.push((arr[i].ts - arr[i - 1].ts) / MS_DAY);
  gaps.sort((a, b) => a - b);
  const med = gaps[Math.floor(gaps.length / 2)];
  if (med <= 2)   return "Daily";
  if (med <= 10)  return "Weekly";
  if (med <= 45)  return "Monthly";
  if (med <= 100) return "Quarterly";
  return "Annual";
}

const showPct = (v, d = 1) =>
  v == null ? "Insufficient data" : `${v > 0 ? "+" : ""}${fmt(v, d)}%`;

// ─── MONETARY REGIME — metric cell ────────────────────────────────────────────
function RegimeStat({ label, value, valueColor, sub, insufficient }) {
  return (
    <div>
      <div style={s.lbl}>{label}</div>
      <div style={{
        fontSize: insufficient ? 12 : 18,
        fontWeight: 700,
        color: insufficient ? C.textDim : (valueColor || C.text),
        lineHeight: 1.15,
      }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: 10, color: C.textDim, marginTop: 2, lineHeight: 1.4 }}>{sub}</div>}
    </div>
  );
}

// ─── MONETARY REGIME MONITOR ──────────────────────────────────────────────────
// Renders only when a FRED key is configured. Fetches the monetary series,
// computes the MV/output gap and credit composition, pushes the result to
// globalSignals via onData("monetaryRegime", ...) for the Signal Board rules.
function MonetaryRegime({ keys, onData }) {
  const [mdata,   setMdata]   = useState(null);
  const [status,  setStatus]  = useState("offline");
  const [loading, setLoading] = useState(false);

  const fetchMonetary = useCallback(async () => {
    if (!keys.fredKey && !window.QUANT_SIGNAL_BACKEND) return;
    setLoading(true);
    const get = async (id, limit) => {
      try {
        const res = await backendFetch(
          `${FRED_BASE}?series_id=${id}` +
          `&api_key=${keys.fredKey}&file_type=json&limit=${limit}&sort_order=desc`,
          `/api/fred/${id}?limit=${limit}`
        );
        const d = await res.json();
        return parseObs(d.observations);
      } catch { return []; }
    };

    try {
      const [m2sl, m2v, mzmv, totll, busloans, gdpc1, gdpnow, mktgdp, wrmfsl] = await Promise.all([
        get("M2SL", 40),     // M2 money supply — monthly
        get("M2V", 12),      // M2 velocity — quarterly
        get("MZMV", 12),     // MZM velocity — quarterly (broader; may be discontinued)
        get("TOTLL", 30),    // total loans & leases
        get("BUSLOANS", 30), // C&I loans
        get("GDPC1", 12),    // real GDP — quarterly
        get("GDPNOW", 8),    // Atlanta Fed GDPNow nowcast (if published to FRED)
        get("GFDEGDQ188S", 8),    // federal debt as % of GDP (fiscal context)
        get("WRMFSL", 8),    // retail money funds (velocity context)
      ]);

      if (!m2sl.length && !m2v.length && !gdpc1.length) {
        setStatus("offline");
        setMdata(null);
        setLoading(false);
        return;
      }

      // ── MV vs output ──
      const m2Growth  = annualizedGrowth(m2sl, 365);   // trailing 4Q (12mo)
      const gdpGrowth = annualizedGrowth(gdpc1, 365);  // trailing 4Q
      const mvSeries  = buildMV(m2sl, m2v);
      const mvGrowth  = annualizedGrowth(mvSeries, 365);
      const mvGap     = (mvGrowth != null && gdpGrowth != null) ? mvGrowth - gdpGrowth : null;

      const m2vLatest = latestOf(m2v);
      const m2vPrior  = m2v.length >= 2 ? m2v[m2v.length - 2] : null;
      const velocityTrend = (m2vLatest && m2vPrior)
        ? (m2vLatest.value > m2vPrior.value ? "recovering"
          : m2vLatest.value < m2vPrior.value ? "declining" : "flat")
        : null;

      // ── Regime classification ──
      let regime, regimeLabel, regimeColor;
      if (mvGap == null)      { regime = "UNKNOWN";        regimeLabel = "Insufficient data";           regimeColor = C.textDim; }
      else if (mvGap > 5)     { regime = "INFLATIONARY";   regimeLabel = "INFLATIONARY CONFIGURATION";   regimeColor = C.red;     }
      else if (mvGap >= 2)    { regime = "ELEVATED";       regimeLabel = "ELEVATED — WATCH";             regimeColor = C.amber;   }
      else if (mvGap >= 0)    { regime = "BALANCED";       regimeLabel = "BALANCED";                     regimeColor = C.green;   }
      else                    { regime = "DISINFLATIONARY";regimeLabel = "DISINFLATIONARY";              regimeColor = C.teal;    }

      // ── Credit composition ──
      const lendingGrowth = annualizedGrowth(totll, 91);    // trailing ~13 weeks
      const ciGrowth      = annualizedGrowth(busloans, 91);
      const creditComposition =
        (ciGrowth != null && lendingGrowth != null && Math.abs(lendingGrowth) > 0.5)
          ? ciGrowth / lendingGrowth : null;
      let creditLabel, creditColor;
      if (creditComposition == null)       { creditLabel = "Insufficient data";               creditColor = C.textDim; }
      else if (creditComposition > 0.5)    { creditLabel = "PRODUCTIVE — muted inflation impact"; creditColor = C.green; }
      else if (creditComposition < 0.3)    { creditLabel = "FINANCIAL — asset inflation risk";    creditColor = C.amber; }
      else                                 { creditLabel = "MIXED";                            creditColor = C.textMid; }

      // ── Fiscal dominance ──
      // No single FRED series publishes the T-bill share of total debt; a precise
      // figure needs the Treasury MSPD (outside FRED). We show real debt/GDP
      // context from MKTGDP and mark bill share unavailable rather than
      // fabricate it from non-bill data. Rule stays dormant until a value exists.
      const debtToGdp = latestOf(mktgdp);
      const billShare = null;

      const computed = {
        m2Growth,   m2Date:  latestOf(m2sl)?.date,   m2Freq:  freqLabel(m2sl),
        velocity:   m2vLatest?.value ?? null,        vDate:   m2vLatest?.date,
        velocityTrend, velocityLatest: m2vLatest?.value ?? null, velocityPrior: m2vPrior?.value ?? null,
        mvGrowth,
        gdpGrowth,  gdpDate: latestOf(gdpc1)?.date,
        gdpNow:     latestOf(gdpnow)?.value ?? null, gdpNowDate: latestOf(gdpnow)?.date,
        mzmv:       latestOf(mzmv)?.value ?? null,   mzmvDate:   latestOf(mzmv)?.date,
        wrmfsl:     latestOf(wrmfsl)?.value ?? null, wrmfslDate: latestOf(wrmfsl)?.date,
        mvGap, regime, regimeLabel, regimeColor,
        lendingGrowth, lendingDate: latestOf(totll)?.date, lendingFreq: freqLabel(totll),
        ciGrowth, ciDate: latestOf(busloans)?.date, ciFreq: freqLabel(busloans),
        creditComposition, creditLabel, creditColor,
        debtToGdp: debtToGdp?.value ?? null, debtDate: debtToGdp?.date,
        billShare,
      };

      setMdata(computed);
      setStatus("delayed"); // FRED is authoritative but not real-time

      // Push to globalSignals for the Signal Board monetary rules.
      if (onData) {
        onData("monetaryRegime", {
          mvGap, lendingGrowth, velocityTrend,
          fiscalDominance: billShare, regime,
          m2Growth, mvGrowth, gdpGrowth,
          velocityLatest: m2vLatest?.value ?? null,
          velocityPrior:  m2vPrior?.value ?? null,
          ciGrowth, creditComposition,
        });
      }
    } catch {
      setStatus("offline");
    }
    setLoading(false);
  }, [keys.fredKey, onData]);

  useEffect(() => { fetchMonetary(); }, [fetchMonetary]);

  if (!keys.fredKey && !window.QUANT_SIGNAL_BACKEND) return null;

  const m = mdata;
  const inflationary    = m?.regime === "INFLATIONARY";
  const disinflationary = m?.regime === "DISINFLATIONARY";

  const interp =
    m?.regime === "INFLATIONARY"
      ? "Asset implications: Hard assets historically outperform in this regime. Long duration bonds historically underperform. Monitor GLD, TLT, XLE, USO in the Markets tab and options flow in OPTIONS tab for confirmation."
      : m?.regime === "DISINFLATIONARY"
      ? "Asset implications: Long duration bonds historically outperform in this regime. Hard assets historically underperform as nominal demand growth trails real output. Monitor TLT, GLD, XLE, USO in the Markets tab and options flow in OPTIONS tab for confirmation."
      : m?.regime === "ELEVATED"
      ? "Configuration is elevated but below the inflationary threshold. Nominal demand growth is running ahead of real output but under the 5pp gap. Watch velocity direction and lending growth for confirmation either way."
      : m?.regime === "BALANCED"
      ? "Nominal demand growth and real output are roughly balanced — neither configuration dominates. Monetary conditions are not currently directing a hard-asset vs duration tilt."
      : "Insufficient history to characterize the monetary regime. Growth rates require enough observations to annualize.";

  const gapColor = m?.regimeColor || C.textDim;
  const billFlag = m?.billShare != null && m.billShare > 20;

  return (
    <div style={s.panel}>
      <div style={s.panelH}>
        <span style={s.panelT}>MONETARY REGIME MONITOR</span>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <DataStatus status={status} source="" />
          <button style={{ ...s.btn(), padding: "3px 7px" }} onClick={fetchMonetary}>
            <RefreshCw size={10} />
          </button>
        </div>
      </div>

      <div style={s.panelB}>
        {loading && !m ? (
          <div style={{ color: C.textDim, fontSize: 13, padding: 20, textAlign: "center" }}>
            COMPUTING MONETARY REGIME...
          </div>
        ) : !m ? (
          <div style={{ color: C.textDim, fontSize: 13, padding: 20, textAlign: "center" }}>
            Data unavailable.
          </div>
        ) : (
          <>
            {/* ── ROW 1 — MV vs Output ── */}
            <div style={{ fontSize: 11, color: C.textDim, letterSpacing: "0.12em", marginBottom: 8 }}>
              MV = PQ · MONEY × VELOCITY vs REAL OUTPUT
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 10 }}>
              <RegimeStat
                label="M2 GROWTH (ANN.)"
                value={showPct(m.m2Growth)}
                valueColor={C.text}
                insufficient={m.m2Growth == null}
                sub={`M2 money supply · ${m.m2Freq || "Monthly"} · ${m.m2Date || "—"}`}
              />
              <RegimeStat
                label="M2 VELOCITY"
                value={m.velocity != null ? fmt(m.velocity, 3) : "Insufficient data"}
                valueColor={C.text}
                insufficient={m.velocity == null}
                sub={`M2 velocity · Quarterly · ${m.vDate || "—"}${m.velocityTrend ? ` · ${m.velocityTrend}` : ""}`}
              />
              <RegimeStat
                label="M×V NOMINAL DEMAND (ANN.)"
                value={showPct(m.mvGrowth)}
                valueColor={C.text}
                insufficient={m.mvGrowth == null}
                sub="Computed: M2 money supply × M2 velocity · Quarterly"
              />
              <RegimeStat
                label="REAL GDP GROWTH (ANN.)"
                value={showPct(m.gdpGrowth)}
                valueColor={C.text}
                insufficient={m.gdpGrowth == null}
                sub={`Real GDP · Quarterly · ${m.gdpDate || "—"}${m.gdpNow != null ? ` · GDP nowcast ${fmt(m.gdpNow, 1)}%` : ""}`}
              />
            </div>

            {/* Gap banner — the key signal */}
            <div style={{
              ...s.card(gapColor),
              borderLeft: `3px solid ${gapColor}`,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 14,
            }}>
              <div>
                <div style={s.lbl}>M×V vs GDP GAP</div>
                <div style={{ fontSize: 24, fontWeight: 700, color: gapColor, lineHeight: 1 }}>
                  {m.mvGap != null ? `${m.mvGap > 0 ? "+" : ""}${fmt(m.mvGap, 1)}pp` : "Insufficient data"}
                </div>
              </div>
              <div style={{
                fontSize: 13, fontWeight: 700, color: gapColor,
                padding: "4px 10px", background: gapColor + "18",
                border: `1px solid ${gapColor}33`, borderRadius: 2, textAlign: "right",
              }}>
                {m.regimeLabel}
              </div>
            </div>

            {/* ── ROW 2 — Credit Composition ── */}
            <div style={{ fontSize: 11, color: C.textDim, letterSpacing: "0.12em", marginBottom: 8 }}>
              CREDIT COMPOSITION · PRODUCTIVE vs FINANCIAL
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: 14 }}>
              <RegimeStat
                label="TOTAL BANK LENDING (ANN.)"
                value={showPct(m.lendingGrowth)}
                valueColor={C.text}
                insufficient={m.lendingGrowth == null}
                sub={`Bank loans & leases · ${m.lendingFreq || "—"} · ${m.lendingDate || "—"}`}
              />
              <RegimeStat
                label="C&I LOAN GROWTH (ANN.)"
                value={showPct(m.ciGrowth)}
                valueColor={C.text}
                insufficient={m.ciGrowth == null}
                sub={`C&I loans · ${m.ciFreq || "—"} · ${m.ciDate || "—"}`}
              />
              <RegimeStat
                label="CREDIT COMPOSITION"
                value={m.creditComposition != null ? `${fmt(m.creditComposition * 100, 0)}% of lending growth` : "Insufficient data"}
                valueColor={m.creditColor}
                insufficient={m.creditComposition == null}
                sub={m.creditLabel}
              />
            </div>

            {/* ── ROW 3 — Fiscal Dominance ── */}
            <div style={{ fontSize: 11, color: C.textDim, letterSpacing: "0.12em", marginBottom: 8 }}>
              FISCAL DOMINANCE · T-BILL SHARE OF FEDERAL DEBT
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10, marginBottom: 14 }}>
              <RegimeStat
                label="FEDERAL DEBT / GDP"
                value={m.debtToGdp != null ? `${fmt(m.debtToGdp, 1)}%` : "Insufficient data"}
                valueColor={C.text}
                insufficient={m.debtToGdp == null}
                sub={`Debt-to-GDP Ratio (Quarterly) · ${m.debtDate || "—"}`}
              />
              <RegimeStat
                label="T-BILL SHARE OF DEBT"
                value={m.billShare != null ? `${fmt(m.billShare, 1)}%` : "Insufficient data"}
                valueColor={m.billShare == null ? C.textDim : billFlag ? C.red : C.green}
                insufficient={m.billShare == null}
                sub={
                  m.billShare == null
                    ? "No single published series reports T-bill share (approximation not shown)"
                    : billFlag
                    ? "FISCAL DOMINANCE RISK — Fed independence constrained"
                    : "NORMAL — Fed retains policy flexibility"
                }
              />
            </div>

            {/* Interpretation panel */}
            <div style={{
              ...s.card(inflationary ? C.red : disinflationary ? C.teal : C.border),
              borderLeft: `3px solid ${inflationary ? C.red : disinflationary ? C.teal : m?.regimeColor || C.border}`,
            }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: m?.regimeColor || C.textMid, letterSpacing: "0.1em", marginBottom: 5 }}>
                REGIME INTERPRETATION — {m.regimeLabel}
              </div>
              <div style={{ fontSize: 13, color: C.textMid, lineHeight: 1.7 }}>
                {interp}
              </div>
            </div>

            <div style={{ fontSize: 11, color: C.textDim, marginTop: 10, lineHeight: 1.6 }}>
              Monetary regime indicators operate on 6-24 month horizons.{" "}
              <span style={{ color: "#FFD700", fontWeight: 700 }}>Not a recommendation — consult a licensed financial advisor.</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── ECONOMY TAB ──────────────────────────────────────────────────────────────
export function EconomyTab({ keys, onData }) {
  const [selected,  setSelected]  = useState(ECON_SERIES[0]);
  const [seriesData,setSeriesData]= useState([]);
  const [latest,    setLatest]    = useState(null);
  const [priorVal,  setPriorVal]  = useState(null);
  const [status,    setStatus]    = useState("offline");
  const [loading,   setLoading]   = useState(false);

  const fetchSeries = useCallback(async (ser) => {
    if (!keys.fredKey && !window.QUANT_SIGNAL_BACKEND) return;
    setLoading(true);
    setSeriesData([]);
    setLatest(null);
    setPriorVal(null);
    setStatus("offline");
    try {
      const res  = await backendFetch(
        `${FRED_BASE}?series_id=${ser.id}` +
        `&api_key=${keys.fredKey}&file_type=json&limit=80&sort_order=desc`,
        `/api/fred/${ser.id}?limit=80`
      );
      const data = await res.json();
      if (data.observations) {
        const obs = data.observations
          .filter((o) => o.value !== ".")
          .reverse();
        const mapped = obs.map((o) => ({
          date:  o.date.slice(0, 7),
          value: parseFloat(o.value),
        }));
        setSeriesData(mapped);
        if (obs.length > 0) setLatest(obs[obs.length - 1]);
        if (obs.length > 1) setPriorVal(obs[obs.length - 2]);
        setStatus("delayed");
      }
    } catch {
      setStatus("offline");
    }
    setLoading(false);
  }, [keys.fredKey]);

  useEffect(() => {
    fetchSeries(selected);
  }, [selected, fetchSeries]);

  // Persist last selected series
  useEffect(() => {
    const saved = localStorage.getItem("fterm_econ_series");
    if (saved) {
      const found = ECON_SERIES.find((s) => s.id === saved);
      if (found) setSelected(found);
    }
  }, []);

  const latestVal  = latest  ? parseFloat(latest.value)  : null;
  const priorValue = priorVal ? parseFloat(priorVal.value) : null;
  const change     = latestVal != null && priorValue != null ? latestVal - priorValue : null;
  const changePct  = change != null && priorValue !== 0 ? (change / priorValue) * 100 : null;

  // Gradient color based on series type
  const seriesColor =
    selected.id.startsWith("DGS")  ? C.teal   :
    selected.id === "UNRATE"        ? C.orange :
    selected.id.includes("CPI") || selected.id.includes("PCE") ? C.amber :
    C.teal;

  const analystContext =
    `FRED economic series: ${selected.label} (${selected.id})\n` +
    `Frequency: ${selected.freq} · Unit: ${selected.unit}\n` +
    `Latest: ${latestVal != null ? fmt(latestVal) : "N/A"} ${selected.unit} as of ${latest?.date || "N/A"}\n` +
    `Prior period: ${priorValue != null ? fmt(priorValue) : "N/A"} ${selected.unit}\n` +
    `Period change: ${change != null ? fmt(change, 2) : "N/A"} (${changePct != null ? fmt(changePct, 2) + "%" : "N/A"})\n` +
    `80-period history loaded.\n\n` +
    `Analyze: the current reading in historical context, the direction and acceleration of the trend, ` +
    `any recent inflection points, and what this level historically implies about the broader macro environment. ` +
    `Be specific about the magnitude and pace of change, not just the direction.`;

  if (!keys.fredKey && !window.QUANT_SIGNAL_BACKEND) {
    return (
      <div style={{ ...s.panel, ...s.panelB, color: C.textDim, fontSize: 14 }}>
        <AlertCircle size={11} style={{ marginRight: 6, verticalAlign: "middle" }} />
        Data unavailable.
      </div>
    );
  }

  return (
    <div>
      {/* Series selector */}
      <div style={{
        display: "flex",
        gap: 5,
        flexWrap: "wrap",
        marginBottom: 10,
      }}>
        {ECON_SERIES.map((ser) => (
          <button
            key={ser.id}
            style={{
              ...s.btn(selected.id === ser.id ? "amber" : "default"),
              minHeight: "44px",
              padding: "8px 12px",
            }}
            onClick={() => {
              setSelected(ser);
              localStorage.setItem("fterm_econ_series", ser.id);
            }}
          >
            {SERIES_DISPLAY_NAMES[ser.id] || ser.id}
          </button>
        ))}
      </div>

      {/* Main series panel */}
      <div style={s.panel}>
        <div style={s.panelH}>
          <div>
            <span style={s.panelT}>{selected.label.toUpperCase()}</span>
            <span style={{ fontSize: 11, color: C.textDim, marginLeft: 8 }}>
              {selected.freq} · {selected.unit}
            </span>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <DataStatus status={status} source="" timestamp={latest?.date} />
            <button
              style={{ ...s.btn(), padding: "3px 7px" }}
              onClick={() => fetchSeries(selected)}
            >
              <RefreshCw size={10} />
            </button>
          </div>
        </div>

        <div style={s.panelB}>
          {/* Latest value + change */}
          {latestVal != null && (
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: 10,
              marginBottom: 14,
              paddingBottom: 10,
              borderBottom: `1px solid ${C.border}`,
            }}>
              <div>
                <div style={s.lbl}>LATEST ({latest?.date})</div>
                <div style={{ fontSize: 24, fontWeight: 700, color: C.amber, lineHeight: 1 }}>
                  {fmt(latestVal)}
                </div>
                <div style={{ fontSize: 13, color: C.textDim, marginTop: 2 }}>{selected.unit}</div>
              </div>
              {priorValue != null && (
                <div>
                  <div style={s.lbl}>PRIOR ({priorVal?.date})</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: C.textMid, lineHeight: 1 }}>
                    {fmt(priorValue)}
                  </div>
                </div>
              )}
              {change != null && (
                <div>
                  <div style={s.lbl}>PERIOD CHANGE</div>
                  <div style={{
                    fontSize: 18,
                    fontWeight: 700,
                    lineHeight: 1,
                    color: change > 0 ? C.green : change < 0 ? C.red : C.textMid,
                  }}>
                    {change > 0 ? "+" : ""}{fmt(change, 2)}
                  </div>
                </div>
              )}
              {changePct != null && (
                <div>
                  <div style={s.lbl}>PCT CHANGE</div>
                  <div style={{
                    fontSize: 18,
                    fontWeight: 700,
                    lineHeight: 1,
                    color: changePct > 0 ? C.green : changePct < 0 ? C.red : C.textMid,
                  }}>
                    {changePct > 0 ? "+" : ""}{fmt(changePct, 2)}%
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Chart */}
          {loading ? (
            <div style={{ color: C.textDim, fontSize: 13, padding: 20, textAlign: "center" }}>
              LOADING...
            </div>
          ) : seriesData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={seriesData} margin={{ top: 4, right: 4, left: 0, bottom: 4 }}>
                    <defs>
                      <linearGradient id="econGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor={seriesColor} stopOpacity={0.18} />
                        <stop offset="95%" stopColor={seriesColor} stopOpacity={0}    />
                      </linearGradient>
                    </defs>
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 10, fill: C.textDim }}
                      tickLine={false}
                      axisLine={false}
                      interval={Math.floor(seriesData.length / 6)}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: C.textDim }}
                      tickLine={false}
                      axisLine={false}
                      domain={["auto", "auto"]}
                      width={50}
                    />
                    <Tooltip
                      contentStyle={{ background: C.panel, border: `1px solid ${C.border}`, fontSize: 13, fontFamily: font }}
                      formatter={(v) => [fmt(v), selected.label]}
                    />
                    <Area
                      type="monotone"
                      dataKey="value"
                      stroke={seriesColor}
                      strokeWidth={1.5}
                      fill="url(#econGrad)"
                      dot={false}
                      activeDot={{ r: 3, fill: seriesColor }}
                    />
                  </AreaChart>
                </ResponsiveContainer>

              {/* Analyst */}
              {(keys.anthropicKey || window.QUANT_SIGNAL_BACKEND) && (
                <div style={{ marginTop: 10 }}>
                  <AnalystButton
                    anthropicKey={keys.anthropicKey}
                    panel="monetary_regime"
                    label="SYNTHESIZE"
                    context={analystContext}
                  />
                </div>
              )}
            </>
          ) : (
            <div style={{ color: C.textDim, fontSize: 13, padding: 20, textAlign: "center" }}>
              No data available for this series
            </div>
          )}
        </div>
      </div>

      {/* Quick reference: all series latest values */}
      <div style={s.panel}>
        <div style={s.panelH}>
          <span style={s.panelT}>MACRO DASHBOARD</span>
          <span style={{ ...s.tag(), fontSize: 11 }}>Click any series above to chart</span>
        </div>
        <div style={{
          ...s.panelB,
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 6,
        }}>
          {ECON_SERIES.filter((ser) => ser.id !== selected.id).slice(0, 8).map((ser) => (
            <button
              key={ser.id}
              onClick={() => {
                setSelected(ser);
                localStorage.setItem("fterm_econ_series", ser.id);
              }}
              style={{
                background: C.panelAlt,
                border: `1px solid ${C.border}`,
                borderRadius: 2,
                padding: "6px 8px",
                cursor: "pointer",
                textAlign: "left",
                fontFamily: font,
              }}
            >
              <div style={{ fontSize: 11, color: C.textDim, marginBottom: 2 }}>{ser.id}</div>
              <div style={{ fontSize: 13, color: C.textMid, lineHeight: 1.3 }}>{ser.label}</div>
              <div style={{ fontSize: 11, color: C.textDim, marginTop: 2 }}>{ser.freq}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Monetary Regime Monitor — MV=PQ gap, credit composition, fiscal dominance */}
      <MonetaryRegime keys={keys} onData={onData} />

      <div style={{ fontSize: 11, color: C.textDim, marginTop: 4 }}>
        80-period history ·{" "}
        <span style={{ color: "#FFD700", fontWeight: 700 }}>Not a recommendation.</span>
      </div>
    </div>
  );
}
